import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import PageShell from './PageShell';
import { RESSOURCES_FICHES } from '../data/ressourcesFiches';
import { ArrowLeft, Download, FileText, Sparkles, CheckCircle2, AlertOctagon, ShieldCheck, Target } from 'lucide-react';

export default function RessourceFichePage() {
  const { slug } = useParams();
  const fiche = RESSOURCES_FICHES[slug];
  if (!fiche) return <Navigate to="/ressources" replace />;

  return (
    <PageShell>
      <section className="max-w-4xl mx-auto px-4 lg:px-6 mt-10 mb-16" data-testid="ressource-fiche-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/ressources" className="hover:text-white transition">Ressources</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">{fiche.category}</span>
        </nav>

        {/* Hero éditorial */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-7 lg:p-10 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-red-600/10 blur-3xl" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-5">
              <FileText size={13} className="text-red-400" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">{fiche.category}</span>
            </div>
            <h1 className="font-serif-editorial text-white text-[32px] sm:text-[40px] lg:text-[48px] leading-[1.02] tracking-tight" data-testid="fiche-title">{fiche.title}</h1>
            <p className="text-white/75 text-[15.5px] lg:text-[16.5px] mt-5 leading-relaxed">{fiche.summary}</p>
            <a
              href={fiche.pdfHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fiche-pdf-download"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition"
            >
              <Download size={13} /> Télécharger le document officiel (PDF)
            </a>
          </div>
        </div>

        {/* Points-clés */}
        <section className="mt-10" data-testid="fiche-key-points">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-emerald-600/15 border border-emerald-700/30 flex items-center justify-center text-emerald-400">
              <Target size={17} />
            </div>
            <h2 className="font-serif-editorial text-white text-[26px] leading-tight">Ce qu'il faut retenir</h2>
          </div>
          <ul className="space-y-3">
            {fiche.keyPoints.map((p, i) => (
              <li key={i} className="rounded-xl border border-white/10 bg-[#0d0d0e] p-4 flex items-start gap-3" data-testid={`fiche-point-${i}`}>
                <CheckCircle2 size={15} className="text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-white/80 text-[14px] leading-relaxed flex-1">{p}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* Pourquoi ça compte pour toi */}
        <section className="mt-10 rounded-2xl border border-red-700/30 bg-red-950/15 p-6 lg:p-7" data-testid="fiche-why-matters">
          <div className="flex items-center gap-2.5 mb-3">
            <Sparkles size={16} className="text-red-400" />
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-300 font-bold font-mono-spec">Pourquoi ça compte pour toi</div>
          </div>
          <p className="text-white/85 text-[15px] italic leading-relaxed">{fiche.whyMatters}</p>
        </section>

        {/* Footer back */}
        <div className="mt-12 pt-6 border-t border-white/5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[11.5px] text-white/40 font-mono-spec inline-flex items-center gap-2">
            <ShieldCheck size={12} /> Fiche éditoriale codevoiture.fr · Source : document officiel ci-dessus
          </div>
          <Link to="/ressources" data-testid="fiche-back-btn" className="inline-flex items-center gap-1.5 text-[13px] text-red-400 hover:text-red-300 font-semibold">
            <ArrowLeft size={13} /> Retour aux ressources
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
